import { useState, useEffect, useCallback } from "react";
import emailjs from "@emailjs/browser";

const SHEET_ID = "1gNIGwnecUujnsgkCu0zJ_iDi3k9S_rin6u7xkDHNva8";
const API_KEY = "AIzaSyBUgx6gvK3vMQP2QqLu_Z60MM0IXu8dytw";
const EMAILJS_SERVICE_ID = "service_385af74";
const EMAILJS_TEMPLATE_ID = "template_6dexyk6";
const EMAILJS_PUBLIC_KEY = "etdWXpjDc00B-QOZQ";
const ORDER_LOG_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbzwsHOvlYrlwtvgkc_30E2vizDOL2pPqOVlNje3ldjuqcfPaDdsFjjVE0YqrQf3_36iqQ/exec";

console.log("App loaded. ORDER_LOG_SCRIPT_URL:", ORDER_LOG_SCRIPT_URL);

const DELIVERY_FEE = 10;
const DELIVERY_THRESHOLD = 50;
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

function lighten(hex, pct) {
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const lr = Math.min(255, Math.round(r + (255 - r) * pct));
    const lg = Math.min(255, Math.round(g + (255 - g) * pct));
    const lb = Math.min(255, Math.round(b + (255 - b) * pct));
    return `#${lr.toString(16).padStart(2, "0")}${lg.toString(16).padStart(2, "0")}${lb.toString(16).padStart(2, "0")}`;
  } catch { return "#eeeeee"; }
}

async function fetchTab(tabName) {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${encodeURIComponent(tabName)}?key=${API_KEY}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not load "${tabName}" tab from Google Sheets.`);
  const data = await res.json();
  return data.values || [];
}

function parseRows(rows) {
  if (!rows || rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  return rows.slice(1)
    .filter((row) => row.some((cell) => cell && cell.trim()))
    .map((row) => {
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (row[i] || "").trim(); });
      return obj;
    });
}

function parsePrices(rows) {
  return parseRows(rows).map((r) => ({
    name: r.name || r.product_name || "",
    sku: r.sku || "",
    price: parseFloat(r.price) || 0,
    category: r.category || "Misc",
  })).filter((p) => p.name && p.sku && p.price > 0);
}

function parseClients(rows) {
  return parseRows(rows).map((r) => {
    const accent = r.accent_color || "#2D6A4F";
    const productSkus = (r.products || "")
      .split(",")
      .map((sku) => sku.trim())
      .filter(Boolean);
    return {
      id: r.client_id || "",
      name: r.client_name || r.client_id || "",
      accent,
      accentLight: lighten(accent, 0.88),
      accentMid: lighten(accent, 0.45),
      logo: (r.client_name || r.client_id || "??").replace(/[^A-Z]/g, "").slice(0, 2) ||
            (r.client_name || "").slice(0, 2).toUpperCase(),
      productSkus: new Set(productSkus),
    };
  }).filter((c) => c.id);
}

function LoadingScreen() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: 16, fontFamily: "system-ui, sans-serif" }}>
      <div style={{ width: 40, height: 40, border: "3px solid #e5e5e5", borderTop: "3px solid #2D6A4F", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <p style={{ color: "#888", fontSize: 14, margin: 0 }}>Loading...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: 12, fontFamily: "system-ui, sans-serif", padding: "2rem" }}>
      <div style={{ fontSize: 36 }}>⚠️</div>
      <p style={{ color: "#B91C1C", fontSize: 15, fontWeight: 500, margin: 0, textAlign: "center" }}>Error: {message}</p>
      <button onClick={() => window.location.reload()} style={{ marginTop: 8, padding: "10px 20px", background: "#2D6A4F", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Try again</button>
    </div>
  );
}

export default function App() {
  const urlClientId = window.location.pathname.replace(/^\//, "").split("/")[0];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [prices, setPrices] = useState([]);
  const [client, setClient] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [contactName, setContactName] = useState("");
  const [deliveryNote, setDeliveryNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [priceRows, clientRows] = await Promise.all([
        fetchTab("Prices"),
        fetchTab("Clients"),
      ]);

      const parsedPrices = parsePrices(priceRows);
      const parsedClients = parseClients(clientRows);

      if (parsedPrices.length === 0) throw new Error("No products in Prices tab");
      if (parsedClients.length === 0) throw new Error("No clients in Clients tab");

      const matchedClient = parsedClients.find((c) => c.id === urlClientId) || parsedClients[0];

      setPrices(parsedPrices);
      setClient(matchedClient);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [urlClientId]);

  useEffect(() => { loadData(); }, [loadData]);

  const setQty = (sku, val) => {
    const n = Math.max(0, parseInt(val) || 0);
    setQuantities((q) => ({ ...q, [sku]: n }));
  };

  const clientProducts = prices.filter((p) => client?.productSkus.has(p.sku));

  const orderItems = clientProducts
    .filter((p) => (quantities[p.sku] || 0) > 0)
    .map((p) => ({ ...p, qty: quantities[p.sku], subtotal: parseFloat((p.price * quantities[p.sku]).toFixed(2)) }));

  const subtotal = parseFloat(orderItems.reduce((s, i) => s + i.subtotal, 0).toFixed(2));
  const needsDeliveryFee = subtotal > 0 && subtotal < DELIVERY_THRESHOLD;
  const deliveryFee = needsDeliveryFee ? DELIVERY_FEE : 0;
  const orderTotal = parseFloat((subtotal + deliveryFee).toFixed(2));
  const itemCount = orderItems.reduce((s, i) => s + i.qty, 0);
  const toFreeDelivery = parseFloat((DELIVERY_THRESHOLD - subtotal).toFixed(2));

  const handleSubmit = async () => {
    if (!orderItems.length || !contactName) return;
    setSubmitting(true);
    setEmailError(false);

    const itemLines = orderItems
      .map((item) => `${item.name} (SKU: ${item.sku}) × ${item.qty}  —  ${fmt(item.subtotal)}`)
      .join("\n");

    const templateParams = {
      client_name: client.name,
      contact_name: contactName,
      order_items: itemLines,
      subtotal: fmt(subtotal),
      delivery_fee: needsDeliveryFee ? fmt(deliveryFee) : "None (free delivery)",
      total: fmt(orderTotal),
      delivery_note: deliveryNote || "None",
      order_date: new Date().toLocaleString(),
    };

    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      setSubmitted(true);

      // Log to Google Sheet
      console.log("Email sent successfully. Logging order with contact name...");
      try {
        const logUrl = `${ORDER_LOG_SCRIPT_URL}?clientName=${encodeURIComponent(client.name)}&contactName=${encodeURIComponent(contactName)}&success=Yes`;
        console.log("Calling log URL:", logUrl);
        const logRes = await fetch(logUrl);
        console.log("Log response status:", logRes.status);
      } catch (logError) {
        console.error("Order log fetch failed:", logError);
      }
    } catch (err) {
      console.error("EmailJS error:", err);
      setEmailError(true);

      // Log failure to Google Sheet
      try {
        const logUrl = `${ORDER_LOG_SCRIPT_URL}?clientName=${encodeURIComponent(client.name)}&contactName=${encodeURIComponent(contactName)}&success=No`;
        console.log("Logging failure to:", logUrl);
        const logRes = await fetch(logUrl);
        console.log("Failure log response:", logRes.status);
      } catch (logError) {
        console.error("Order log fetch failed:", logError);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingScreen />;
  if (error) return <ErrorScreen message={error} />;
  if (!client) return <ErrorScreen message="Client not found" />;

  const { accent: a, accentLight: al, accentMid: am } = client;
  const canSubmit = orderItems.length > 0 && contactName;

  if (submitted) {
    return (
      <div style={{ padding: "1rem", maxWidth: 500, margin: "0 auto", fontFamily: "system-ui, sans-serif" }}>
        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: "2rem 1.5rem", textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: al, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem", fontSize: 28, color: a }}>✓</div>
          <h2 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 0.5rem" }}>Order submitted!</h2>
          <p style={{ color: "#666", fontSize: 14, margin: "0 0 1.5rem" }}>{client.name} · {itemCount} items · {fmt(orderTotal)}</p>
          <div style={{ background: "#f9f9f9", borderRadius: 8, padding: "1rem", textAlign: "left", marginBottom: "1rem" }}>
            {orderItems.map((item) => (
              <div key={item.sku} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#555", padding: "4px 0", borderBottom: "1px solid #eee" }}>
                <span>{item.name} (SKU: {item.sku}) × {item.qty}</span>
                <span>{fmt(item.subtotal)}</span>
              </div>
            ))}
            {needsDeliveryFee && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "4px 0", borderBottom: "1px solid #eee" }}>
                <span style={{ color: "#B45309" }}>Delivery fee</span>
                <span style={{ color: "#B45309" }}>{fmt(deliveryFee)}</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600, fontSize: 16, paddingTop: 8, marginTop: 4 }}>
              <span>Total</span>
              <span style={{ color: a }}>{fmt(orderTotal)}</span>
            </div>
          </div>
          <p style={{ fontSize: 13, color: "#888", margin: "0 0 1.5rem" }}>✉ Order alert sent to dfawkes@gocitywide.com</p>
          <button onClick={() => { setSubmitted(false); setQuantities({}); setDeliveryNote(""); setContactName(""); }} style={{ background: a, color: "#fff", border: "none", borderRadius: 10, padding: "14px 24px", fontSize: 16, fontWeight: 500, cursor: "pointer", width: "100%", minHeight: 52 }}>
            Place another order
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", maxWidth: 500, margin: "0 auto" }}>
      <div style={{ background: "#fff", padding: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: "12px 12px 0 0", border: "1px solid #eee" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: al, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, color: a, flexShrink: 0 }}>{client.logo}</div>
          <div>
            <p style={{ margin: 0, fontWeight: 600, fontSize: 16 }}>{client.name}</p>
            <p style={{ margin: 0, fontSize: 12, color: "#888" }}>Supply Order Form</p>
          </div>
        </div>
        <img src="/cwfs-logo.jpeg" alt="City Wide Facilities Solutions" style={{ height: 40, width: "auto", objectFit: "contain" }} />
      </div>

      <div style={{ background: al, padding: "8px 1rem", borderLeft: "1px solid #eee", borderRight: "1px solid #eee" }}>
        <span style={{ fontSize: 12, color: a }}>📊 {clientProducts.length} products available</span>
      </div>

      {subtotal > 0 && subtotal < DELIVERY_THRESHOLD && (
        <div style={{ background: "#FEF3C7", padding: "10px 1rem", borderLeft: "1px solid #eee", borderRight: "1px solid #eee", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#92400E" }}>⚠ Under {fmt(DELIVERY_THRESHOLD)} — {fmt(DELIVERY_FEE)} fee applies</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#92400E", whiteSpace: "nowrap" }}>+{fmt(toFreeDelivery)} to waive</span>
        </div>
      )}
      {subtotal >= DELIVERY_THRESHOLD && subtotal > 0 && (
        <div style={{ background: "#D1FAE5", padding: "10px 1rem", borderLeft: "1px solid #eee", borderRight: "1px solid #eee" }}>
          <span style={{ fontSize: 13, color: "#065F46", fontWeight: 500 }}>✓ Free delivery on this order</span>
        </div>
      )}

      <div style={{ background: "#fff", borderLeft: "1px solid #eee", borderRight: "1px solid #eee", padding: "1rem" }}>
        {clientProducts.length === 0 && (
          <p style={{ color: "#888", fontSize: 14, textAlign: "center", padding: "2rem 0" }}>No products for this client</p>
        )}
        {clientProducts.map((product) => {
          const qty = quantities[product.sku] || 0;
          return (
            <div key={product.sku} style={{ display: "flex", alignItems: "center", padding: "12px", marginBottom: 6, borderRadius: 10, background: qty > 0 ? al : "#f9f9f9", border: `1px solid ${qty > 0 ? am : "#e5e5e5"}`, gap: 8, minHeight: 64 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: qty > 0 ? 600 : 400, lineHeight: 1.3 }}>{product.name}</p>
                <p style={{ margin: "2px 0 0", fontSize: 13, fontWeight: 500, color: qty > 0 ? a : "#888" }}>{fmt(product.price)}</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <button onClick={() => setQty(product.sku, qty - 1)} style={{ width: 44, height: 44, borderRadius: 10, background: qty > 0 ? a : "#fff", color: qty > 0 ? "#fff" : "#888", border: `1px solid ${qty > 0 ? a : "#ccc"}`, fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer" }}>−</button>
                <div style={{ width: 36, textAlign: "center", fontSize: 16, fontWeight: 600, color: qty > 0 ? a : "#888" }}>{qty || "0"}</div>
                <button onClick={() => setQty(product.sku, qty + 1)} style={{ width: 44, height: 44, borderRadius: 10, background: a, color: "#fff", border: "none", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer" }}>+</button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ background: "#fff", border: "1px solid #eee", borderTop: `3px solid ${a}`, borderRadius: "0 0 12px 12px", padding: "1rem" }}>
        {orderItems.length > 0 && (
          <div style={{ marginBottom: "1rem", background: "#f9f9f9", borderRadius: 10, padding: "1rem" }}>
            <p style={{ margin: "0 0 0.5rem", fontSize: 13, fontWeight: 600 }}>Order summary</p>
            {orderItems.map((item) => (
              <div key={item.sku} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#555", padding: "3px 0" }}>
                <span>{item.name} (SKU: {item.sku}) × {item.qty}</span>
                <span>{fmt(item.subtotal)}</span>
              </div>
            ))}
            <div style={{ borderTop: "1px solid #e5e5e5", marginTop: 6, paddingTop: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#666", padding: "2px 0" }}>
                <span>Subtotal</span><span>{fmt(subtotal)}</span>
              </div>
              {needsDeliveryFee && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "2px 0" }}>
                  <span style={{ color: "#B45309" }}>Delivery fee</span>
                  <span style={{ color: "#B45309" }}>{fmt(deliveryFee)}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600, fontSize: 16, marginTop: 6, paddingTop: 6, borderTop: "1px solid #e5e5e5" }}>
                <span>Total</span><span style={{ color: a }}>{fmt(orderTotal)}</span>
              </div>
            </div>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, color: "#666", display: "block", marginBottom: 6, fontWeight: 500 }}>Your name *</label>
          <input value={contactName} onChange={(ev) => setContactName(ev.target.value)} placeholder="Jane Smith" style={{ fontSize: 16, padding: "12px 14px", borderRadius: 10, minHeight: 48, width: "100%", border: "1px solid #ddd", background: "#f9f9f9", fontFamily: "inherit" }} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: "#666", display: "block", marginBottom: 6, fontWeight: 500 }}>Special delivery instructions</label>
          <textarea value={deliveryNote} onChange={(ev) => setDeliveryNote(ev.target.value)} placeholder="Leave at side entrance, call on arrival, etc." rows={3} style={{ fontSize: 16, padding: "12px 14px", borderRadius: 10, resize: "vertical", minHeight: 80, width: "100%", fontFamily: "inherit", border: "1px solid #ddd", background: "#f9f9f9" }} />
        </div>

        {emailError && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#B91C1C" }}>
            ⚠ Could not send email. Try again.
          </div>
        )}

        <button onClick={handleSubmit} disabled={!canSubmit || submitting} style={{ width: "100%", padding: "16px", background: canSubmit ? a : "#e5e5e5", color: canSubmit ? "#fff" : "#aaa", border: "none", borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed", minHeight: 56 }}>
          {submitting ? "Submitting..." : orderItems.length ? `Submit order · ${itemCount} items · ${fmt(orderTotal)}` : "Add items to place order"}
        </button>

        <p style={{ fontSize: 12, color: "#aaa", textAlign: "center", margin: "0.75rem 0 0" }}>
          Orders logged to {client.name} Google Sheet
        </p>
      </div>
    </div>
  );
}
